import { getConfig, isConfigSet, getOauthClientId } from '@local/app-config';
import { trackError } from '@local/metrics';
import { v4 as uuid } from 'uuid';

import { LOGIN_SUCCESS_ROUTE, LOGIN_SILENT, type TokenScope } from '../constants';
import { setCSRFState } from '../store/sessionStorageHelpers/authStateHelpers';
import { setPkceVerifier } from '../store/sessionStorageHelpers/pkceFlowHelpers';
import { getPkceChallenge } from './getPkceChallenge';

export async function getAuthUrl(forceOauthPrefix: boolean, additionalScopes: TokenScope[]) {
    let baseUrl = '';
    if (isConfigSet()) {
        const config = getConfig();
        baseUrl = config.identity_url;
    }
    if (baseUrl?.length > 0) {
        // can't know the URL without the config. Error message displayed by <LoginError />
        const clientId = getOauthClientId(forceOauthPrefix);
        const redirectUrl = `${window.location.origin}${LOGIN_SUCCESS_ROUTE}` || '';
        const authUrl = new URL(`${baseUrl}/oauth2/authz`);
        const query = authUrl.searchParams || new URLSearchParams();
        const defaultScopes: TokenScope[] = ['openid', 'profile', 'email'];

        // Security feature to defend against CSRF attacks
        // We just need a cryptographically secure random string, we can later verify in LoginSuccess
        const state = uuid();
        setCSRFState(state);
        query.append('response_type', 'code');
        query.append('client_id', clientId);
        query.append('redirect_uri', redirectUrl);
        query.append('state', state);
        query.append('scope', [...defaultScopes, ...additionalScopes].join(' '));

        const { codeChallenge, codeVerifier } = await getPkceChallenge();
        query.append('code_challenge', codeChallenge);
        query.append('code_challenge_method', 'S256');
        setPkceVerifier(codeVerifier);

        authUrl.search = query.toString();
        return authUrl;
    }
    trackError('Error getting config for getAuthUrl');
    // Without config, so just reload page & hope for the best
    window.location.reload();
    return undefined;
}

export interface GetBentleyAuthUrlInterface {
    forceOauthPrefix: boolean;
    additionalScopes: TokenScope[];
    tabId?: string;
    silent?: boolean;
    extendSession?: boolean;
}

/** Builds an auth url for Bentley IMS.
 * The request is sent for Bentley IMS **GET /connect/authorize** endpoint with the following parameters:
 * client_id, scope, state, redirect_uri, code_challenge, code_challenge_method, and optionally prompt=none
 * @param {GetBentleyAuthUrlInterface} args
 * @param {boolean} args.forceOauthPrefix
 * @param {TokenScope[]} args.additionalScopes scopes to request from our OICD client
 * @param {string} [args.tabId] a `uuid` assigned to a tab for storing the CSRF state and PKCE verifier that prevents multiple tabs from colliding
 * @param {boolean} [args.silent] attaches the `prompt=none` query param to silently authenticate
 * @param {boolean} [args.extendSession] changes the redirectUrl to our silent route, useful to avoid loading the whole app on redirect
 * @returns
 */
export async function getBentleyAuthUrl({
    forceOauthPrefix,
    additionalScopes,
    tabId,
    silent,
    extendSession,
}: Readonly<GetBentleyAuthUrlInterface>) {
    let baseUrl = '';
    if (isConfigSet()) {
        const config = getConfig();
        baseUrl = config.bentleyBaseUrl;
    }
    if (baseUrl?.length > 0) {
        const clientId = getOauthClientId(forceOauthPrefix);
        const redirectUrl = extendSession
            ? `${window.location.origin}${LOGIN_SILENT}`
            : `${window.location.origin}${LOGIN_SUCCESS_ROUTE}`;

        const url = `${baseUrl}/connect/authorize`;

        const authUrl = new URL(url);
        const query = authUrl.searchParams || new URLSearchParams();
        const defaultScopes = ['openid', 'profile', 'email'];

        // Security feature to defend against CSRF attacks
        // We just need a cryptographically secure random string, we can later verify in LoginSuccess
        const state = uuid();
        setCSRFState(state, tabId);
        query.append('response_type', 'code');
        query.append('client_id', clientId);
        query.append('redirect_uri', redirectUrl);
        query.append('state', state);
        query.append('scope', [...defaultScopes, ...additionalScopes].join(' '));

        if (silent) {
            query.append('prompt', 'none');
        }

        const { codeChallenge, codeVerifier } = await getPkceChallenge();
        query.append('code_challenge', codeChallenge);
        query.append('code_challenge_method', 'S256');
        setPkceVerifier(codeVerifier, tabId);

        authUrl.search = query.toString();
        return authUrl;
    }
    trackError('Error getting config for getBentleyAuthUrl');
    // Without config, so just reload page & hope for the best
    window.location.reload();
    return undefined;
}
